import { Menu } from '@headlessui/react';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useUserContext } from '../../providers/UserProvider';
import { Workpaper } from '../../types/workpaper';
import { formatDate } from '../../utilities/formatting';
import { useEffect, useRef, useState } from 'react';
import { useIsFirmRole } from '../../hooks/use-is-firm-role';
import { TooltipV2 } from '@cimplico/components';

type WorkpaperQuickNavigateWorkpaperDetailsProps = {
    workpaper: Workpaper;
    current?: boolean;
};

function WorkpaperQuickNavigateWorkpaperDetails({ workpaper, current }: WorkpaperQuickNavigateWorkpaperDetailsProps): JSX.Element {
    const [isOverflowed, setIsOverflow] = useState(false);
    const { shortId } = useFirmContext();
    const { id } = useUserContext();
    const { isAdmin } = useIsFirmRole();
    const textElementRef = useRef(null);

    const canAccess = isAdmin ? true : workpaper.private ? workpaper.assigned.find((a) => a.id === id) : true;
    const canClick = canAccess && !current;

    useEffect(() => {
        if (textElementRef.current) {
            // @ts-ignore
            setIsOverflow(textElementRef.current.scrollWidth > textElementRef.current.clientWidth);
        }
    }, [workpaper.name]);

    const menuItem = (
        <Menu.Item disabled={!canClick}>
            {({ active }): JSX.Element => (
                <button
                    disabled={!canClick}
                    className={`mt-1 flex flex-col pb-2 ${!canClick && 'cursor-default'} ${
                        (active || current) && 'dark:bg-white/[0.09] bg-gray-100 text-gray-900 dark:text-white/[0.87]'
                    }`}
                >
                    <TooltipV2 message={workpaper.name} disabled={!isOverflowed}>
                        <div className="flex justify-between pt-2 px-2 w-full">
                            <div className="flex items-center gap-1">
                                <p className="font-medium text-indigo-600 dark:text-indigo-400">
                                    {formatDate(new Date(workpaper.periodEnd))}
                                </p>
                                {workpaper.private && <LockClosedIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                            </div>
                            {current && <p className="text-gray-500 dark:text-gray-400 text-xs">Current</p>}
                        </div>
                        <div className="w-[250px]">
                            <p ref={textElementRef} className="text-gray-500 dark:text-gray-400 px-2 text-left truncate">
                                {workpaper.name}
                            </p>
                        </div>
                    </TooltipV2>
                </button>
            )}
        </Menu.Item>
    );

    return canClick ? <Link to={{ pathname: `/${shortId}/workpapers/${workpaper.id}/dashboard` }}>{menuItem}</Link> : <>{menuItem}</>;
}

export default WorkpaperQuickNavigateWorkpaperDetails;
